
import React, { useEffect, useRef, useState } from 'react';
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import type { GetMontonioMethodsResponse } from '@/dto/index';
import type { Bank, MontonioPaymentMethod } from './montonio/types';
import PaymentForm from './montonio/PaymentForm';
import { usePaymentProcessor } from './montonio/usePaymentProcessor';
import { toast } from "@/components/ui/use-toast";
import { isValidMontonioPaymentMethod } from './montonio/validators';

interface MontonioPaymentInterfaceProps {
  onSubmit: (paymentInfo?: { paymentMethod: MontonioPaymentMethod; selectedBank: string | null }) => void;
  amount: string;
  isTestMode?: boolean;
  setupData?: any;
  isRedirecting?: boolean;
}

const MontonioPaymentInterface = ({ 
  onSubmit, 
  amount, 
  isTestMode = false,
  setupData,
  isRedirecting = false
}: MontonioPaymentInterfaceProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<any>(null);
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [availableBanks, setAvailableBanks] = useState<Bank[]>([]);

  const { isProcessing, handlePaymentSubmit } = usePaymentProcessor({ onSubmit });

  useEffect(() => {
    console.log("💥 MontonioPaymentInterface: Komponent laeti");
    async function fetchPaymentMethods() {
      try {
        console.log("💥 MontonioPaymentInterface: Maksemeetodite pärimine");
        const data = await apiFetch<GetMontonioMethodsResponse>('/api/payments/montonio/methods');

        if (!data?.payment_methods) {
          console.error("💥 MontonioPaymentInterface: Maksemeetodeid ei saadud:", data);
          throw new Error('Maksemeetodeid ei saadud vastuses');
        }

        console.log("💥 MontonioPaymentInterface: Maksemeetodite vastus:", data);
        setPaymentMethods(data.payment_methods);
      } catch (err) {
        console.error('💥 MontonioPaymentInterface: Viga maksemeetodite pärimisel:', err);
        setError('Maksemeetodite laadimine ebaõnnestus: ' + (err instanceof Error ? err.message : String(err)));
      } finally {
        setIsLoading(false);
      }
    }

    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (!paymentMethods?.paymentInitiation?.setup?.EE?.paymentMethods) return;

    const banks = getBanksFromPaymentMethods(paymentMethods);
    console.log("✅ Leitud pangad:", banks);
    setAvailableBanks(banks);

    if (banks.length > 0 && !selectedBank) {
      setSelectedBank(banks[0].id);
    }
  }, [paymentMethods]);

  const getBanksFromPaymentMethods = (methods: any): Bank[] => {
    const eeBanks = methods?.paymentInitiation?.setup?.EE?.paymentMethods;
    if (Array.isArray(eeBanks)) {
      return eeBanks.map((bank: any) => ({
        id: bank.code ?? 'unknown',
        name: bank.name ?? 'Nimetu pank',
        logo: bank.logoUrl ?? '',
      }));
    }
    console.warn("💥 Eesti panku ei leitud. Maksemeetodite struktuur:", methods);
    return [];
  };

  const handleBankSelect = (bankId: string) => {
    console.log("💥 MontonioPaymentInterface: Pank valitud:", bankId);
    setSelectedBank(bankId);
  };

  const handleSubmit = () => {
    console.log("💥 MontonioPaymentInterface: Makse esitamine käivitatud");

    if (!selectedBank) {
      console.error("💥 MontonioPaymentInterface: Pank valimata");
      setError("Palun vali pank enne jätkamist");
      toast({
        title: "Viga",
        description: "Palun vali pank enne jätkamist",
        variant: "destructive"
      });
      return;
    }

    console.log("💥 MontonioPaymentInterface: Jätkamine pangaga:", selectedBank);
    try {
      // Use the proper typed payment method
      const paymentMethod: MontonioPaymentMethod = "paymentInitiation";
      
      // Validate the payment method before proceeding
      if (!isValidMontonioPaymentMethod(paymentMethod)) {
        throw new Error(`Kehtetu maksemeetod: ${paymentMethod}`);
      }
      
      handlePaymentSubmit(selectedBank, paymentMethod);
    } catch (err) {
      console.error("💥 MontonioPaymentInterface: Viga makse esitamisel:", err);
      setError("Makse esitamine ebaõnnestus: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (isRedirecting) {
    return (
      <Card className="p-6">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-center text-gray-700">Suunan makseteenuse pakkuja lehele...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <div id="montonio-checkout" className="mb-4"></div>
          
          <PaymentForm
            ref={formRef}
            availableBanks={availableBanks}
            selectedBank={selectedBank}
            onBankSelect={handleBankSelect}
            onSubmit={handleSubmit}
            isProcessing={isProcessing}
          />
        </>
      )}
    </Card>
  );
};

export default MontonioPaymentInterface;
